import { useRecoilCallback } from 'recoil'
import { v4 as uuidv4 } from 'uuid'
import type { EditorTextBoxInput } from '@messenger/lotus/row-store'
import {
  getEditorPageContent,
  saveEditorPageContent,
} from '@/api/editorPageContent'
import {
  editorPageContentAtomFamily,
  editorTextBoxDocumentsSelector,
  editorTextBoxDraftsAtom,
} from '@/atoms/editorTextBoxAtoms'
import {
  canvasPageIdAtom,
  selectedItemsAtom,
  unsavedChangesAtom,
} from '@/atoms/_editorAtoms'
import { useSyncCurrentPageToEditorPages } from '@/hooks/editor/useSyncCurrentPageToEditorPages'
import { useErrorHandler } from '@/hooks/utility/useErrorHandler'
import { editorTextBoxDocumentToInput } from '@/utils/editor-text-box/editorTextBoxAdapter'
import { logger, EventCategory } from '@/utils/logger'

export const useEditorDuplicateTextBox = () => {
  const syncCurrentPageToEditorPages = useSyncCurrentPageToEditorPages()
  const handleError = useErrorHandler('useEditorDuplicateTextBox')

  return useRecoilCallback(
    ({ snapshot, set }) =>
      async (sourceBoxId: string): Promise<string | null> => {
        const pageId = await snapshot.getPromise(canvasPageIdAtom)
        if (!pageId) return null
        const [results, drafts] = await Promise.all([
          snapshot.getPromise(editorTextBoxDocumentsSelector(pageId)),
          snapshot.getPromise(editorTextBoxDraftsAtom),
        ])
        const source =
          drafts[sourceBoxId] ?? results?.get(sourceBoxId)?.document ?? null
        if (source === null) {
          handleError(
            'The text box could not be duplicated',
            new Error(`no document for box ${sourceBoxId}`),
          )
          return null
        }
        const { expectedRevision: _dropped, ...sourceBox } =
          editorTextBoxDocumentToInput(source) as EditorTextBoxInput & {
            expectedRevision?: number
          }
        void _dropped
        const boxId = uuidv4()
        // A duplicate is a new box: it sets no lock of its own and follows
        // the page's lock setting, whatever the source's session lock says.
        const box: EditorTextBoxInput = {
          ...sourceBox,
          boxId,
          x: (sourceBox.x ?? 0) + 10,
          y: (sourceBox.y ?? 0) + 10,
        }
        try {
          await saveEditorPageContent(pageId, {
            boxes: [box],
            deleteBoxIds: [],
          })
          const { data: bundle } = await getEditorPageContent(pageId)
          await syncCurrentPageToEditorPages()
          set(editorPageContentAtomFamily(pageId), bundle)
          set(selectedItemsAtom, [boxId])
          set(unsavedChangesAtom, true)
        } catch (error) {
          handleError(
            'The text box could not be duplicated',
            error instanceof Error ? error : new Error(String(error)),
          )
          return null
        }
        logger.info({
          message: 'editor_duplicate_text_box_success',
          category: EventCategory.INSERT,
          data: { pageId, sourceBoxId, boxId },
        })
        return boxId
      },
    [syncCurrentPageToEditorPages, handleError],
  )
}

export default useEditorDuplicateTextBox
